import { ScheduledEmailApi, scheduledEmailsApi } from "api/scheduledemails/scheduledemailsApi";
import { QUERIES_KEYS } from "queries";
import { useQuery, useQueryClient, keepPreviousData, useMutation } from "@tanstack/react-query";

export const useScheduledEmails = (
  skip: number,
  limit: number,
  searchPhrase: string = "",
  filter: string = "",
  orderBy: string = ""
) => {
  const queryClient = useQueryClient();
  const { isLoading, isFetching, data } = useQuery({
    queryKey: QUERIES_KEYS.SCHEDULED_EMAILS(skip, limit, searchPhrase, filter, orderBy),
    queryFn: () => scheduledEmailsApi.fetch(skip, limit, searchPhrase, filter, orderBy),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.SCHEDULED_EMAILS(skip, limit, searchPhrase, filter, orderBy),
    });
  };

  return {
    invalidate,
    isLoading,
    isFetching,
    scheduledEmails: data?.data.data ?? [],
    total: data?.data.total ?? 0,
  };
};

export const useScheduledEmail = (id: number) => {
  const { isLoading, data, error } = useQuery({
    queryKey: QUERIES_KEYS.SCHEDULED_EMAIL(id),
    queryFn: () => scheduledEmailsApi.fetchById(id),
    enabled: !!id,
    staleTime: 30000,
  });

  return {
    isLoading,
    scheduledEmail: data?.data,
    error,
  };
};

export const useScheduledEmailRecipients = (id: number) => {
  const { isLoading, data, error } = useQuery({
    queryKey: QUERIES_KEYS.SCHEDULED_EMAIL_RECIPIENTS(id),
    queryFn: () => scheduledEmailsApi.fetchRecipients(id),
    enabled: !!id,
    staleTime: 30000,
  });

  return {
    isLoading,
    recipients: data?.data ?? [],
    error,
  };
};

export const useCreateScheduledEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: scheduledEmailsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-emails"] });
    },
  });
};

export const useCreateNewsletterSend = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: scheduledEmailsApi.createNewsletter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-emails"] });
    },
  });
};

export const useUpdateScheduledEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => scheduledEmailsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-emails"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-email", variables.id] });
    },
  });
};

export const useScheduleEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, deliveryTime }: { id: number; deliveryTime: string }) =>
      scheduledEmailsApi.schedule(id, deliveryTime),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-emails"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-email", variables.id] });
    },
  });
};

export const useSendEmailImmediately = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: scheduledEmailsApi.sendImmediately,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-emails"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-email", id] });
    },
  });
};

export const useCancelScheduledEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: scheduledEmailsApi.cancel,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-emails"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-email", id] });
    },
  });
};

export const useDeleteScheduledEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: scheduledEmailsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-emails"] });
    },
  });
};